import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { PublicLayout } from '@/layouts/PublicLayout';
import { PrivateLayout } from '@/layouts/PrivateLayout';
import { AdminLayout } from '@/layouts/AdminLayout';
import { LoginPage } from '@/pages/LoginPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { AhDashboardPage } from '@/pages/AhDashboardPage';
import { PrijsfavorietenDashboardPage } from '@/pages/PrijsfavorietenDashboardPage';
import { MonopolyDashboardPage } from '@/pages/MonopolyDashboardPage';
import { MonopolyWinnersPage } from '@/pages/MonopolyWinnersPage';
import { SurveyDashboardPage } from '@/pages/SurveyDashboardPage';
import { SurveyWinnersPage } from '@/pages/SurveyWinnersPage';
import { MinionsDashboardPage } from '@/pages/MinionsDashboardPage';
import { MorrisonsDashboardPage } from '@/pages/MorrisonsDashboardPage';
import { AdminUsersPage } from '@/pages/AdminUsersPage';
import { AdminPipelineRunsPage } from '@/pages/AdminPipelineRunsPage';
import { AdminPipelineConfigPage } from '@/pages/AdminPipelineConfigPage';
import { DASHBOARD_CONFIGS, landingPath, type DashboardConfig } from '@/features/dashboard/config';
import { useAuth } from '@/features/auth/authStore';

/** Each report's `kind` picks its dashboard shell. */
function DashboardRoute({ config }: { config: DashboardConfig }) {
  if (config.kind === 'ah') return <AhDashboardPage config={config} />;
  if (config.kind === 'prijsfavorieten') return <PrijsfavorietenDashboardPage config={config} />;
  if (config.kind === 'monopoly') return <MonopolyDashboardPage config={config} />;
  if (config.kind === 'survey') return <SurveyDashboardPage config={config} />;
  if (config.kind === 'minions') return <MinionsDashboardPage config={config} />;
  if (config.kind === 'morrisons') return <MorrisonsDashboardPage config={config} />;
  return <DashboardPage config={config} />;
}

function HomeRedirect() {
  const auth = useAuth();
  // No report in the URL: the first entry of the user's report switcher.
  const target = auth?.token ? landingPath(auth.allowedReports, auth.role === 'admin') : '/login';
  return <Navigate to={target} replace />;
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public routes */}
        <Route element={<PublicLayout />}>
          <Route path="/login" element={<LoginPage />} />
        </Route>

        {/* Admin routes */}
        <Route element={<AdminLayout />}>
          <Route path="/admin/users" element={<AdminUsersPage />} />
          <Route path="/admin/runs" element={<AdminPipelineRunsPage />} />
          <Route path="/admin/config" element={<AdminPipelineConfigPage />} />
        </Route>

        {/* Authenticated dashboard routes */}
        <Route element={<PrivateLayout />}>
          {DASHBOARD_CONFIGS.map((cfg) => (
            <Route key={cfg.path} path={cfg.path} element={<DashboardRoute config={cfg} />} />
          ))}
          {/* Winners is a PAGE beside the Monopoly dashboard, not a tab inside
              it: different audience, live data, and actions of its own to come. */}
          {DASHBOARD_CONFIGS.filter((cfg) => cfg.kind === 'monopoly').map((cfg) => (
            <Route key={`${cfg.path}/winners`} path={`${cfg.path}/winners`} element={<MonopolyWinnersPage config={cfg} />} />
          ))}
          {/* Same for the Système U quiz: the weekly lottery winners, to complete and approve. */}
          {DASHBOARD_CONFIGS.filter((cfg) => cfg.kind === 'survey').map((cfg) => (
            <Route key={`${cfg.path}/winners`} path={`${cfg.path}/winners`} element={<SurveyWinnersPage config={cfg} />} />
          ))}
        </Route>

        {/* Catch-all */}
        <Route path="*" element={<HomeRedirect />} />
      </Routes>
    </BrowserRouter>
  );
}
